import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { REACT_APP_API_URL } from '../../../env';
import EmployeeViewEditModal from '../AllEmployees/Components/EmployeeViewEditModal';
import axios from 'axios';

const EmployeeProfile = () => {
  const { hrmsUser, userType } = useSelector((state) => state.auth);
  const navigate = useNavigate();


  const [employeeData, setEmployeeData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userType !== 'hrms') {
      navigate('/hrms/login');
    }
  }, [userType, navigate]);

  const fetchFullEmployee = useCallback(async () => {
    try {
      const res = await axios.get(`${REACT_APP_API_URL}api/hrms/get-employee/${hrmsUser.id}`, {
        withCredentials: true,
      });
      // console.log("Fetched employee:", res.data);
      setEmployeeData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hrmsUser?.id]);

  useEffect(() => {
    if (userType === 'hrms' && hrmsUser) {
      fetchFullEmployee();
    }
  }, [userType, hrmsUser, fetchFullEmployee]);

  const openModal = (mode = "view") => {
    setEditMode(mode === "edit");
    setShowModal(true);
  };

  const imageSrc =
    employeeData?.vchUserImage &&
      employeeData.vchUserImage.trim() !== ""
      ? `${REACT_APP_API_URL}${employeeData.vchUserImage.replace(/\\/g, "/")}`
      : "/images/avatar.png";

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="card border-0 shadow-sm rounded-4 px-4 py-5 text-center">
          <div className="spinner-border text-primary mx-auto mb-3" role="status" aria-hidden="true"></div>
          <h5 className="mb-1">Loading profile</h5>
          <small className="text-muted">Fetching the latest employee information.</small>
        </div>
      </div>
    );
  }

  if (!employeeData) {
    return (
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body text-center py-5">
          <i className="bi bi-person-exclamation fs-1 text-muted mb-3 d-block"></i>
          <h5 className="mb-2">No employee data found</h5>
          <p className="text-muted mb-0">We could not load the current profile details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-xl-4 px-3">
      <div className="row g-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-header bg-transparent border-0 d-flex flex-wrap justify-content-between align-items-center gap-2 pt-4 px-4">
              <div>
                <h4 className="card-title mb-1">My Profile</h4>
                <small className="text-muted">View and edit your profile details</small>
              </div>
              <button
                className="btn btn-sm btn-outline-warning px-3"
                onClick={() => openModal("edit")}
              >
                Edit Profile
              </button>
            </div>

            <div className="card-body p-4">
              <div className="row g-4 align-items-start">
                <div className="col-lg-4 text-center">
                  <div className="bg-body-tertiary rounded-4 p-4 h-100">
                    <img
                      src={imageSrc}
                      alt={employeeData.vchFullName}
                      className="rounded-circle border mb-3 shadow-sm"
                      width="136"
                      height="136"
                      style={{ objectFit: "cover" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/avatar.png";
                      }}
                    />
                    <h5 className="mb-1">{employeeData.vchFullName}</h5>
                    <p className="text-muted mb-0">{employeeData.vchDesignation}</p>
                  </div>
                </div>

                <div className="col-lg-8">
                  <div className="card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body p-4">
                      <div className="row g-3">
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Employee ID</label>
                          <div className="fw-semibold">{employeeData.vchEmpCode}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Full Name</label>
                          <div className="fw-semibold">{employeeData.vchFullName}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Email</label>
                          <div className="fw-semibold text-break">{employeeData.vchEmail}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Phone</label>
                          <div className="fw-semibold">{employeeData.vchMobTel}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Gender</label>
                          <div className="fw-semibold">{employeeData.vchGender}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Address</label>
                          <div className="fw-semibold">{employeeData.vchPreAddress}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Department/Unit</label>
                          <div className="fw-semibold">{employeeData.vchDepartment}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Company</label>
                          <div className="fw-semibold">{employeeData.vchCompanyName}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Date of Birth</label>
                          <div className="fw-semibold">{employeeData.dtmDob?.split("T")[0]}</div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label fw-bold text-muted small mb-1">Date of Joining</label>
                          <div className="fw-semibold">{employeeData.dtmDoj?.split("T")[0]}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for View/Edit */}
      {showModal && (
        <EmployeeViewEditModal
          show={showModal}
          onClose={() => setShowModal(false)}
          employee={employeeData}
          editMode={editMode}
          setEditMode={setEditMode}
          onSuccess={(updatedEmployee) => {
            if (updatedEmployee) {

              setEmployeeData({
                ...updatedEmployee,
                dtmDob: updatedEmployee.dtmDob ? new Date(updatedEmployee.dtmDob).toISOString() : null,
                dtmDoj: updatedEmployee.dtmDoj ? new Date(updatedEmployee.dtmDoj).toISOString() : null,
              });
            } else {
              fetchFullEmployee();
            }
          }}
        />
      )}
    </div>
  );
};

export default EmployeeProfile;
